import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { map, startWith } from 'rxjs';
import { AccountService } from './account.service';
import { UserProfileDTO } from '../models/user-profile.model';
import { CreatePatRequest, PatDTO } from '../models/pat.model';
import { PatCreateDialogComponent } from './pat-create-dialog/pat-create-dialog.component';
import { ConfirmDialogComponent } from '../components/confirm-dialog/confirm-dialog.component';
import { SectionTitleComponent } from '../components/section-title/section-title.component';
import { ProviderTokensSectionComponent } from '../components/provider-tokens-section/provider-tokens-section.component';
import { GithubTokenSectionComponent } from '../components/github-token-section/github-token-section.component';
import { RunnerPairingSectionComponent } from '../components/runner-pairing-section/runner-pairing-section.component';
import { KiMcpBlockComponent } from './ki-mcp-block/ki-mcp-block.component';

@Component({
  selector: 'app-account',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    PatCreateDialogComponent,
    ConfirmDialogComponent,
    MatProgressSpinner,
    MatButton,
    MatIconButton,
    MatIcon,
    DatePipe,
    SectionTitleComponent,
    ProviderTokensSectionComponent,
    GithubTokenSectionComponent,
    RunnerPairingSectionComponent,
    KiMcpBlockComponent,
  ],
  templateUrl: './account.component.html',
  styleUrl: './account.component.scss',
})
export class AccountComponent implements OnInit {
  private accountService = inject(AccountService);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  private readonly snackSuccess: MatSnackBarConfig = { panelClass: 'snackbar-success' };
  private readonly snackError: MatSnackBarConfig = { panelClass: 'snackbar-error' };

  readonly profile = signal<UserProfileDTO | null>(null);
  readonly profileLoading = signal(true);
  readonly browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  protected readonly tokensHeadingId = 'account-tokens-heading';
  protected readonly runnerPairingHeadingId = 'account-runner-pairing-heading';
  protected readonly providerTokensHeadingId = 'account-provider-tokens-heading';
  protected readonly githubTokenHeadingId = 'account-github-token-heading';

  readonly tokens = signal<PatDTO[]>([]);
  readonly tokensLoading = signal(true);
  readonly createDialogVisible = signal(false);
  readonly createdToken = signal<string | null>(null);
  readonly isCreatingToken = signal(false);
  readonly revokeTarget = signal<PatDTO | null>(null);
  readonly isRevoking = signal(false);

  readonly activeTokens = computed(() => this.tokens().filter((token) => token.revokedAt === null));

  readonly currentLanguageLabel = toSignal(
    this.translate.onLangChange.pipe(
      startWith(null),
      map(() => {
        const lang = this.translate.currentLang ?? 'de';
        return this.translate.instant(lang === 'de' ? 'languageMenu.german' : 'languageMenu.english');
      }),
    ),
    { initialValue: '' },
  );

  readonly initials = computed(() => {
    const parts = (this.profile()?.displayName ?? '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return parts[0][0].toUpperCase() + parts[parts.length - 1][0].toUpperCase();
  });

  ngOnInit(): void {
    this.accountService
      .getProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (profile) => {
          this.profile.set(profile);
          this.profileLoading.set(false);
        },
        error: () => {
          this.profileLoading.set(false);
          this.snackBar.open(this.translate.instant('account.profileLoadError'), undefined, this.snackError);
        },
      });

    this.loadTokens();
  }

  private loadTokens(): void {
    this.tokensLoading.set(true);
    this.accountService
      .listTokens()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tokens) => {
          this.tokens.set(tokens);
          this.tokensLoading.set(false);
        },
        error: () => {
          this.tokensLoading.set(false);
          this.snackBar.open(this.translate.instant('pat.loadError'), undefined, this.snackError);
        },
      });
  }

  openCreateDialog(): void {
    this.createdToken.set(null);
    this.createDialogVisible.set(true);
  }

  onCreateDialogVisibleChange(visible: boolean): void {
    this.createDialogVisible.set(visible);
    if (!visible) {
      this.createdToken.set(null);
    }
  }

  onCreatePat(request: CreatePatRequest): void {
    this.isCreatingToken.set(true);
    this.accountService
      .createToken(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isCreatingToken.set(false);
          this.createdToken.set(response.token);
          this.tokens.update((tokens) => [response.pat, ...tokens]);
        },
        error: (error: unknown) => {
          this.isCreatingToken.set(false);
          const key =
            error instanceof HttpErrorResponse && error.status === 409 ? 'pat.createNameExists' : 'pat.createError';
          this.snackBar.open(this.translate.instant(key), undefined, this.snackError);
        },
      });
  }

  requestRevoke(token: PatDTO): void {
    this.revokeTarget.set(token);
  }

  cancelRevoke(): void {
    this.revokeTarget.set(null);
  }

  confirmRevoke(): void {
    const target = this.revokeTarget();
    if (!target) return;
    this.isRevoking.set(true);
    this.accountService
      .revokeToken(target.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isRevoking.set(false);
          this.tokens.update((tokens) => tokens.filter((token) => token.id !== target.id));
          this.revokeTarget.set(null);
          this.snackBar.open(this.translate.instant('pat.revokeSuccess'), undefined, this.snackSuccess);
        },
        error: () => {
          this.isRevoking.set(false);
          this.revokeTarget.set(null);
          this.snackBar.open(this.translate.instant('pat.revokeError'), undefined, this.snackError);
        },
      });
  }
}
